import { v4 as uuid } from 'uuid';

import {
  AutomationNodeTypes,
  AutomationTrigger,
  CreateAutomationApiArg,
  UpdateAutomationApiArg,
} from 'apis/automations';
import {
  MatchingConversationType,
  MatchingOperator,
  MatchingStrategy,
  VariableScope,
} from 'components/automations/components/nodeTypes/types';

import { AutomationModalFormValues } from './automationModal.types';

export const getUpdatePayload = (
  automationId: string | null,
  values: AutomationModalFormValues,
): UpdateAutomationApiArg | null => {
  if (!automationId) {
    return null;
  }

  const updatePayload: UpdateAutomationApiArg = {
    automationId,
    payload: {
      name: values.name,
      description: values.description,
    },
  };

  return updatePayload;
};

export const getCreatePayload = (
  values: AutomationModalFormValues,
  defaultQueueId: string | null,
  defaultQueueName: string | null,
  active: boolean = false,
): CreateAutomationApiArg | null => {
  const stopNodeID = uuid();

  if (
    values.trigger === null ||
    values.channels === null ||
    values.name === ''
  ) {
    return null;
  }

  const channels =
    values.trigger === AutomationTrigger.EXTERNAL_TRIGGER
      ? []
      : values.channels;

  const createPayload: CreateAutomationApiArg = {
    name: values.name,
    description: values.description,
    trigger: values.trigger,
    channels,
    active,
    actions: [
      {
        id: 'root',
        pageX: 0,
        pageY: 0,
        nodeType: AutomationNodeTypes.INIT,
        parentId: null,
        childIds: [stopNodeID],
        connectionLabel: '',
        payload: {
          matchNodeId: stopNodeID,
          matchingStrategy: MatchingStrategy.ALL,
          rules:
            defaultQueueId === null || defaultQueueName === null
              ? []
              : [
                  {
                    variable: {
                      scope: VariableScope.SYSTEM,
                      variableType: MatchingConversationType.QUEUE,
                    },
                    operator: MatchingOperator.INCLUDES_ONE,
                    match: {
                      scope: VariableScope.INPUT,
                      values: [
                        {
                          value: defaultQueueId,
                          meta: {
                            label: defaultQueueName,
                          },
                        },
                      ],
                    },
                  },
                ],
        },
        isRemovable: false,
        isEditable: true,
      },
      {
        id: stopNodeID,
        pageX: 0,
        pageY: 150,
        nodeType: AutomationNodeTypes.STOP,
        parentId: 'root',
        childIds: [],
        connectionLabel: 'arrow',
        payload: {},
        isRemovable: true,
        isEditable: false,
      },
    ],
  };

  return createPayload;
};

export const getPayload = (
  automationId: string | null,
  values: AutomationModalFormValues,
  mode: 'create' | 'update',
  defaultQueueId: string,
  defaultQueueName: string,
): CreateAutomationApiArg | UpdateAutomationApiArg | null => {
  const stopNodeID = uuid();
  if (mode === 'update') {
    if (!automationId) {
      return null;
    }

    const updatePayload: UpdateAutomationApiArg = {
      automationId,
      payload: {
        name: values.name,
        description: values.description,
      },
    };

    return updatePayload;
  }

  if (mode === 'create') {
    if (values.trigger === null || values.name === '') {
      return null;
    }

    const createPayload: CreateAutomationApiArg = {
      name: values.name,
      description: values.description,
      trigger: values.trigger,
      channels: values.channels,
      active: false,
      actions: [
        {
          id: 'root',
          pageX: 0,
          pageY: 0,
          nodeType: AutomationNodeTypes.INIT,
          parentId: null,
          childIds: [stopNodeID],
          connectionLabel: '',
          payload: {
            matchNodeId: stopNodeID,
            matchingStrategy: MatchingStrategy.ALL,
            rules: [
              {
                variable: {
                  scope: VariableScope.SYSTEM,
                  variableType: MatchingConversationType.QUEUE,
                },
                operator: MatchingOperator.INCLUDES_ONE,
                match: {
                  scope: VariableScope.INPUT,
                  values: [
                    {
                      value: defaultQueueId,
                      meta: {
                        label: defaultQueueName,
                      },
                    },
                  ],
                },
              },
            ],
          },
          isRemovable: false,
          isEditable: true,
        },
        {
          id: stopNodeID,
          pageX: 0,
          pageY: 150,
          nodeType: AutomationNodeTypes.STOP,
          parentId: 'root',
          childIds: [],
          connectionLabel: 'arrow',
          payload: {},
          isRemovable: true,
          isEditable: false,
        },
      ],
    };

    return createPayload;
  }

  return null;
};
